import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "../services/api";
import { fetchGoogleCandidates } from "../services/googlePlaceService";

/** "1.2 km" / "350 m" — how far a candidate is from the branch's pin. */
const formatDistance = (metres, t) =>
  metres >= 1000
    ? t("googlePlace.distanceKm", { value: (metres / 1000).toFixed(1) })
    : t("googlePlace.distanceM", { value: metres });

/**
 * Search Google for the branch's place and pick one. Searches once on open with
 * the prefilled query; the owner can refine it. `onConfirm(candidate)` gets
 * `{ place_id, name, address }` — what happens next is the caller's business.
 */
export default function GooglePlaceSearchModal({
  open,
  initialQuery,
  lat,
  lng,
  busy,
  error,
  onConfirm,
  onClose,
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState(initialQuery ?? "");
  const [candidates, setCandidates] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchError, setSearchError] = useState("");

  const search = async (text) => {
    if (!text.trim()) return;
    setSearching(true);
    setSearchError("");
    setChosen(null);
    try {
      const result = await fetchGoogleCandidates({ q: text.trim(), lat, lng });
      setCandidates(result.candidates ?? []);
    } catch (err) {
      setCandidates([]);
      setSearchError(apiErrorMessage(err, t("googlePlace.searchFailed")));
    } finally {
      setSearching(false);
      setSearched(true);
    }
  };

  useEffect(() => {
    if (!open) return;
    setQuery(initialQuery ?? "");
    setCandidates([]);
    setSearched(false);
    search(initialQuery ?? "");
    // Searching on open only; typing searches on submit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-xl bg-surface shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <h2 className="text-lg font-semibold">{t("googlePlace.findTitle")}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-sm text-ink-2 hover:text-ink"
          >
            {t("common.close")}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Not a <form>: this modal opens from inside the branch form, and a
              nested form would submit the branch. Enter searches instead. */}
          <div className="mb-4 flex gap-2">
            <input
              className="field-input flex-1"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  search(query);
                }
              }}
              aria-label={t("googlePlace.searchLabel")}
            />
            <button
              type="button"
              className="btn-secondary"
              disabled={searching || !query.trim()}
              onClick={() => search(query)}
            >
              {t("googlePlace.search")}
            </button>
          </div>

          {(searchError || error) && (
            <div className="mb-3 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
              {error || searchError}
            </div>
          )}

          {searching ? (
            <p className="py-6 text-center text-sm text-ink-2">{t("common.loading")}</p>
          ) : searched && candidates.length === 0 ? (
            <p className="py-6 text-center text-sm text-ink-2">{t("googlePlace.noResults")}</p>
          ) : (
            <ul className="space-y-2">
              {candidates.map((candidate) => (
                <li key={candidate.place_id}>
                  <label
                    className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2 ${
                      chosen?.place_id === candidate.place_id ? "border-accent bg-accent-wash" : "border-line"
                    }`}
                  >
                    <input
                      type="radio"
                      name="google-place"
                      className="mt-1"
                      checked={chosen?.place_id === candidate.place_id}
                      onChange={() => setChosen(candidate)}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium">{candidate.name ?? candidate.place_id}</span>
                      {candidate.address && (
                        <span className="block text-xs text-ink-2">{candidate.address}</span>
                      )}
                    </span>
                    {candidate.distance_m != null && (
                      <span className="whitespace-nowrap text-xs text-ink-2">
                        {formatDistance(candidate.distance_m, t)}
                      </span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
          <button type="button" onClick={onClose} disabled={busy} className="btn-secondary">
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!chosen || busy}
            onClick={() => onConfirm(chosen)}
          >
            {busy ? t("common.saving") : t("googlePlace.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
